import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import { FlowChartProvider } from '@/components/diagrams/FlowChart';
import { PreviewWithBrandingToggle } from '@/components/diagrams/FlowChart/components/Controls/PreviewWithBrandingToggle';
import { FlowChartWrapper } from '@/components/diagrams/FlowChart/components/FlowChartWrapper';
import { useFlowChartContext } from '@/components/diagrams/FlowChart/context/flowChart.context';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';
import { Button } from '@/components/form/baseInputs/Button';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { FamilyTreeContextContainer } from '@/modules/familyTree/components/FamilyTreeContextContainer';
import {
  FamilyTreeProvider,
  useFamilyTreeContext,
} from '@/modules/familyTree/contexts';
import { FamilyTreeFlowChart } from '@/modules/familyTree/FamilyTreeFlowChart';
import {
  Operations,
  useGetHouseholdFamilyTreeQuery,
} from '@/modules/familyTree/graphql/FamilyTree.generated';
import { useUpdateFamilyTreeSettingsMutation } from '@/modules/familyTree/graphql/FamilyTreeMutations.generated';
import { useRemoveHiddenIndividuals } from '@/modules/familyTree/hooks/useRemoveHiddenIndividuals';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { CustomerThemeProvider } from '@/styles/themes/CustomerThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';

const TROWSER_HEADER_HEIGHT = 68;
const MIN_HEIGHT = 720;

interface EditFamilyTreeTrowserInnerProps {
  householdId: string;
  onClose: (didSave?: boolean) => void;
}

function EditFamilyTreeTrowserInner({
  householdId,
  onClose,
}: EditFamilyTreeTrowserInnerProps) {
  const { dispatch, state } = useFamilyTreeContext();
  const { isDirty } = useFlowChartContext();
  const { toggleIsInteractive, getIsInteractive } = useIsInteractive();
  const { showFeedback } = useFeedback();
  const [isSaving, setIsSaving] = useState(false);

  // Refetch (and await) the family tree query so the saved layout is loaded
  // before we close — otherwise the presentation slide remounts and re-scales
  // against stale positions.
  const [updateFamilyTreeSettings] = useUpdateFamilyTreeSettingsMutation({
    refetchQueries: [Operations.Query.GetHouseholdFamilyTree],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!getIsInteractive()) {
      toggleIsInteractive();
    }
  }, [getIsInteractive, toggleIsInteractive]);

  useOnBeforeUnloadPrompt(isDirty);

  const handleSave = useCallback(async () => {
    if (!isDirty) {
      onClose(false);
      return;
    }

    setIsSaving(true);

    // Build node configurations from current state, filtering out synthetic nodes
    const nodeConfigurations = state.nodes
      .filter((node) => {
        if (node.type === 'person') {
          return !node.data.person.isSynthetic;
        }
        return true;
      })
      .map((node) => ({
        nodeID: node.id,
        xPosition: node.position.x,
        yPosition: node.position.y,
      }));

    try {
      await updateFamilyTreeSettings({
        variables: {
          householdId,
          settings: {
            nodeConfigurations,
          },
        },
      });

      showFeedback('Changes saved successfully.', { variant: 'success' });

      setIsSaving(false);
      onClose(true);
    } catch (err) {
      showFeedback('Something went wrong. Please try again.');
      diagnostics.error('Failed to save family tree', err as Error, {
        householdId,
      });
      setIsSaving(false);
    }
  }, [
    isDirty,
    state.nodes,
    updateFamilyTreeSettings,
    householdId,
    showFeedback,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        height={TROWSER_HEADER_HEIGHT}
        minHeight={TROWSER_HEADER_HEIGHT}
        borderBottom={1}
        borderColor={COLORS.GRAY[200]}
      >
        <Typography variant="title-l">Edit diagram</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <PreviewWithBrandingToggle
            checked={state.renderWhiteLabeled}
            onChange={(checked) =>
              dispatch({
                type: 'SET_RENDER_WHITE_LABELED',
                renderWhiteLabeled: checked,
              })
            }
          />
          <Stack direction="row" spacing={1}>
            <Button variant="secondary" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={isSaving}
              data-testid="edit-family-tree-save-button"
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Box flex={1} minHeight={0}>
        <LoadingOverlay
          open={!state.familyTreeData}
          updatingText="Loading..."
        />
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <CustomerThemeProvider
              forceLuminaryTheme={!state.renderWhiteLabeled}
            >
              <FamilyTreeContextContainer
                height={Math.max(MIN_HEIGHT, height)}
                width={width}
              >
                <FlowChartWrapper
                  style={{ height: Math.max(MIN_HEIGHT, height) }}
                  variant="transparent"
                >
                  {state.familyTreeData && (
                    <FamilyTreeFlowChart
                      presentationMode={false}
                      hideFullscreenButton
                    />
                  )}
                </FlowChartWrapper>
              </FamilyTreeContextContainer>
            </CustomerThemeProvider>
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
}

export interface EditFamilyTreeTrowserProps {
  onClose: (didSave?: boolean) => void;
  householdId: string;
}

export function EditFamilyTreeTrowser({
  onClose,
  householdId,
}: EditFamilyTreeTrowserProps) {
  const { showFeedback } = useFeedback();

  const { data, loading } = useGetHouseholdFamilyTreeQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      householdId,
      clientProfileWhere: {
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
      },
    },
    onError: (error) => {
      showFeedback(FeedbackMessages.queryError);
      diagnostics.error('family tree load failure', error, {
        householdId,
      });
    },
  });

  const { familyTreeData, clientProfiles } = useRemoveHiddenIndividuals(data);

  if (loading && !familyTreeData) {
    return (
      <FullScreenModal
        trowserKey={TrowserKey.EditFamilyTree}
        isOpen
        onClose={() => onClose()}
      >
        <LoadingOverlay open updatingText="Loading..." />
      </FullScreenModal>
    );
  }

  return (
    <FullScreenModal
      trowserKey={TrowserKey.EditFamilyTree}
      isOpen
      onClose={() => onClose()}
      backdropSx={{ overflow: 'hidden' }}
    >
      <FlowChartProvider key={householdId}>
        <FamilyTreeProvider
          householdId={householdId}
          familyTreeData={familyTreeData}
          clientProfiles={clientProfiles}
          presentationMode={false}
          initialRenderWhiteLabeled
        >
          <EditFamilyTreeTrowserInner
            householdId={householdId}
            onClose={onClose}
          />
        </FamilyTreeProvider>
      </FlowChartProvider>
    </FullScreenModal>
  );
}
